import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ErrorHandlerService } from './error-handler.service';
import { AuthService } from './auth.service';

export type NotificationType =
  | 'conversation_intervention_needed'
  | 'system_error'
  | 'appointment_new'
  | 'appointment_cancelled_or_rejected'
  | 'appointment_modified'
  | 'tenant_new_member';

interface ApiResponse<T> {
  message: string;
  data?: T;
}

export interface NotificationItem {
  id: string;
  tenantId: string;
  type: NotificationType;
  conversationId?: string;
  title: string;
  description?: string;
  createdAt: string;
  readAt?: string | null;
  metadata?: { errorCode?: string; appointmentId?: string; diningReservationId?: string };
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly apiUrl = environment.apiUrl;

  private getHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }

  getNotifications(options?: {
    unreadOnly?: boolean;
    types?: NotificationType[];
    page?: number;
    pageSize?: number;
  }): Observable<{ items: NotificationItem[]; total: number }> {
    let params = new HttpParams();
    if (options?.unreadOnly === true) params = params.set('unreadOnly', 'true');
    else if (options?.unreadOnly === false) params = params.set('unreadOnly', 'false');
    if (options?.types?.length) params = params.set('types', options.types.join(','));
    if (options?.page) params = params.set('page', String(options.page));
    if (options?.pageSize) params = params.set('pageSize', String(options.pageSize));
    const shouldSendParams = params.keys().length > 0;
    return this.http
      .get<ApiResponse<{ items: NotificationItem[]; total: number }>>(
        `${this.apiUrl}/notifications`,
        {
          headers: this.getHeaders(),
          params: shouldSendParams ? params : undefined,
        }
      )
      .pipe(
        map((res) => res.data ?? { items: [], total: 0 }),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.logError(error);
          return throwError(() => error);
        })
      );
  }

  getUnreadNotificationsCount(): Observable<number> {
    return this.http
      .get<ApiResponse<{ unreadCount: number }>>(`${this.apiUrl}/notifications/unread-count`, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((res) => res.data?.unreadCount ?? 0),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.logError(error);
          return throwError(() => error);
        })
      );
  }

  markAsRead(notificationId: string): Observable<void> {
    return this.http
      .patch<
        ApiResponse<void>
      >(`${this.apiUrl}/notifications/${notificationId}/read`, {}, { headers: this.getHeaders() })
      .pipe(
        map(() => void 0),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.logError(error);
          return throwError(() => error);
        })
      );
  }
}
